/**
 * Business Environment Factor Detail Route
 *
 * Displays comprehensive business environment factor metadata
 */

import { useState } from 'react';
import { createFileRoute, useNavigate, Outlet, useLocation } from '@tanstack/react-router';
import { useAllTags, useBusinessEnvironmentFactor } from '../../graphql/queries';
import { useApolloClient } from '@apollo/client/react';
import {
  DetailSkeleton,
  EmptyState,
  DeleteConfirmationDialog,
  PermissionGate,
} from '../../components/shared';
import { TagBadge } from '../../components/tags/TagBadge';
import { deleteBusinessEnvironmentFactor, ApiError } from '../../lib/apiClient';
import '@tylertech/forge/card';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import '@tylertech/forge/divider';
import './businessEnvironmentFactorId.css';

export const Route = createFileRoute('/business-environment-factors/$businessEnvironmentFactorId')({
  component: BusinessEnvironmentFactorDetailPage,
});

export { BusinessEnvironmentFactorDetailPage };

function BusinessEnvironmentFactorDetailPage(): JSX.Element {
  const { businessEnvironmentFactorId } = Route.useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const apolloClient = useApolloClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If on a child route (edit), render only the outlet
  const isChildRoute = location.pathname.endsWith('/edit');

  // Fetch business environment factor data
  const {
    data,
    loading,
    error: fetchError,
  } = useBusinessEnvironmentFactor(businessEnvironmentFactorId);
  const businessEnvironmentFactor = data?.businessEnvironmentFactor;

  // Resolve the entity's stored tag catalog ids to full Tag records for
  // display (issue #773 — Tags now come from the governed catalog via
  // TagPicker rather than free-text entry). Uses the full catalog (not a
  // single capped page, issue #1352) via `useAllTags` — a plain `useTags()`
  // page can miss an assigned tag whose id sorts past the first page, which
  // silently drops it from `resolvedTags` rather than surfacing a fetch
  // error, matching the pattern already used by
  // `TechnologyCapabilityDetailPage`.
  const { tags: allTags } = useAllTags();
  const resolvedTags = (businessEnvironmentFactor?.tags ?? [])
    .map((tagId) => allTags.find((tag) => tag.entityId === tagId))
    .filter((tag): tag is NonNullable<typeof tag> => tag !== undefined);

  const CardElement = 'forge-card' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const DividerElement = 'forge-divider' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  if (isChildRoute) {
    return <Outlet />;
  }

  // Loading state
  if (loading) {
    return (
      <div className="business-environment-factor-detail-page">
        <DetailSkeleton cards={3} />
      </div>
    );
  }

  // Error state
  if (fetchError) {
    return (
      <div className="business-environment-factor-detail-page">
        <EmptyState
          icon="error"
          title="Failed to load business environment factor"
          description={fetchError.message}
          actionLabel="Back to Business Environment Factors"
          onAction={() =>
            navigate({ to: '/business-environment-factors', search: { page: 1, pageSize: 25 } })
          }
        />
      </div>
    );
  }

  // Not found state
  if (!businessEnvironmentFactor) {
    return (
      <div className="business-environment-factor-detail-page">
        <EmptyState
          icon="search_off"
          title="Business environment factor not found"
          description={`No business environment factor found with ID: ${businessEnvironmentFactorId}`}
          actionLabel="Back to Business Environment Factors"
          onAction={() =>
            navigate({ to: '/business-environment-factors', search: { page: 1, pageSize: 25 } })
          }
        />
      </div>
    );
  }

  // Format dates
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Handle edit action
  const handleEdit = (): void => {
    navigate({
      to: `/business-environment-factors/${businessEnvironmentFactor.entityId}/edit` as string,
    });
  };

  // Handle delete action
  const handleDelete = (): void => {
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleConfirmDelete = async (): Promise<void> => {
    setIsDeleting(true);
    setError(null);

    try {
      // Soft delete by default
      await deleteBusinessEnvironmentFactor(businessEnvironmentFactorId, false);

      // Refetch GraphQL data to update cache
      await apolloClient.refetchQueries({
        include: ['ListBusinessEnvironmentFactors'],
      });

      // Navigate back to business environment factors list
      navigate({ to: '/business-environment-factors', search: { page: 1, pageSize: 25 } });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsDeleting(false);
    }
  };

  return (
    <div className="business-environment-factor-detail-page">
      {/* Header */}
      <div className="business-environment-factor-detail-header">
        <div>
          <ForgeButton
            type="button"
            variant="text"
            onClick={() =>
              navigate({ to: '/business-environment-factors', search: { page: 1, pageSize: 25 } })
            }
          >
            <IconElement name="arrow_back" aria-hidden="true" />
            Back to Business Environment Factors
          </ForgeButton>
          <h1 className="forge-typography--headline4">{businessEnvironmentFactor.name}</h1>
          <p className="forge-typography--body2 page-header-subtitle">
            {businessEnvironmentFactor.description || 'No description provided'}
          </p>
        </div>
        <div className="business-environment-factor-detail-actions">
          <PermissionGate permission="business-environment-factor:update">
            <ForgeButton type="button" variant="raised" onClick={handleEdit}>
              <IconElement name="edit" aria-hidden="true" />
              Edit
            </ForgeButton>
          </PermissionGate>
          <PermissionGate permission="business-environment-factor:delete">
            <ForgeButton type="button" variant="outlined" onClick={handleDelete}>
              <IconElement name="delete" aria-hidden="true" />
              Delete
            </ForgeButton>
          </PermissionGate>
        </div>
      </div>

      <div className="business-environment-factor-detail-content">
        {/* Overview Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Overview</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Business Environment Factor ID</span>
              <span className="detail-value">{businessEnvironmentFactor.entityId}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Category</span>
              <span className="detail-value">{businessEnvironmentFactor.category || '—'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Label</span>
              <span className="detail-value">{businessEnvironmentFactor.label || '—'}</span>
            </div>
          </div>
        </CardElement>

        {/* Additional Information Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Additional Information</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Tags</span>
              <span className="detail-value">
                {resolvedTags.length > 0 ? (
                  <span className="business-environment-factor-detail-tags">
                    {resolvedTags.map((tag) => (
                      <TagBadge key={tag.entityId} tag={tag} />
                    ))}
                  </span>
                ) : (
                  '—'
                )}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Notes</span>
              <span className="detail-value">{businessEnvironmentFactor.notes || '—'}</span>
            </div>
          </div>
        </CardElement>

        {/* Metadata Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Metadata</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Created</span>
              <span className="detail-value">
                {formatDate(businessEnvironmentFactor._createdAt)} by{' '}
                {businessEnvironmentFactor._createdBy}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Last Updated</span>
              <span className="detail-value">
                {formatDate(businessEnvironmentFactor._updatedAt)} by{' '}
                {businessEnvironmentFactor._updatedBy}
              </span>
            </div>
          </div>
        </CardElement>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteDialogOpen(false)}
          title="Delete Business Environment Factor"
          message="Are you sure you want to delete this business environment factor?"
          warning="This action will soft delete the business environment factor. It can be recovered by an administrator."
          itemName={businessEnvironmentFactor.name}
          isDeleting={isDeleting}
        />
      )}

      {/* Error Banner */}
      {error && <div className="detail-error-toast">{error}</div>}
    </div>
  );
}
